/**
 * Database Queries
 *
 * Read operations for spaces, folders, lists, and items.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import type { Project, Document, Chat, VoiceChannel, Spreadsheet, Presentation, SpaceRecord } from './types';
import { convertSpaceFromSupabase, getRootProjectId } from './helpers';
import { handleError } from '@/utils/errorHandler';

export async function getProjectIdByName(projectName: string, orgId?: string): Promise<string | null> {
  const { data: project } = await withRetry(() => {
    let query = supabase
      .from('spaces')
      .select('id')
      .eq('type', 'project')
      .eq('name', projectName);

    if (orgId) {
      query = query.eq('org_id', orgId);
    }

    return query.single();
  });

  return project?.id ?? null;
}

export async function getProjectIdByPath(path: string): Promise<string | null> {
  const pathParts = path.split('/');
  if (pathParts.length === 0) return null;

  // Start from root projects
  let currentId: string | null = null;

  // Find root project
  const { data: rootProjects } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id')
      .eq('type', 'project')
      .eq('name', pathParts[0])
      .limit(1)
  );

  if (!rootProjects || rootProjects.length === 0) return null;
  currentId = rootProjects[0].id;

  // Traverse hierarchy
  for (let i = 1; i < pathParts.length; i++) {
    const { data: children } = await withRetry(() =>
      supabase
        .from('spaces')
        .select('id')
        .eq('parent_id', currentId)
        .eq('name', pathParts[i])
        .limit(1)
    );

    if (!children || children.length === 0) return null;
    currentId = children[0].id;
  }

  return currentId;
}

export async function getFolderIdByName(projectId: string, folderName: string): Promise<string | null> {
  const { data: folder } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id')
      .eq('type', 'folder')
      .eq('parent_id', projectId)
      .eq('name', folderName)
      .single()
  );
  return folder?.id ?? null;
}

export async function getListIdByName(parentId: string, listName: string): Promise<string | null> {
  const { data: list } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id')
      .eq('type', 'list')
      .eq('parent_id', parentId)
      .eq('name', listName)
      .single()
  );
  return list?.id ?? null;
}

export async function getProjectNameById(projectId: string): Promise<string | null> {
  const { data: project } = await withRetry(() => supabase.from('spaces').select('name').eq('id', projectId).single());
  return project?.name ?? null;
}

export async function getFolderNameById(folderId: string): Promise<string | null> {
  const { data: folder } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('name')
      .eq('id', folderId)
      .eq('type', 'folder')
      .single()
  );
  return folder?.name ?? null;
}

export async function getListNameById(listId: string): Promise<string | null> {
  const { data: list } = await withRetry(() => supabase.from('spaces').select('name').eq('id', listId).eq('type', 'list').single());
  return list?.name ?? null;
}

export async function getFolderSpaceId(folderId: string): Promise<string | null> {
  const { data: folder } = await withRetry(() => supabase.from('spaces').select('project_id').eq('id', folderId).single());
  return folder?.project_id ?? null;
}

export async function getListSpaceId(listId: string): Promise<string | null> {
  const { data: list } = await withRetry(() => supabase.from('spaces').select('project_id').eq('id', listId).single());
  return list?.project_id ?? null;
}

export async function getListFolderId(listId: string): Promise<string | null> {
  const { data: list } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('parent_id')
      .eq('id', listId)
      .eq('type', 'list')
      .single()
  );
  // Check if parent is a folder
  if (list?.parent_id) {
    const { data: parent } = await withRetry(() => supabase.from('spaces').select('type').eq('id', list.parent_id).single());
    return parent?.type === 'folder' ? list.parent_id : null;
  }
  return null;
}

export async function getFoldersByProjectId(projectId: string): Promise<Array<{ id: string; name: string }>> {
  const { data: folders } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id, name')
      .eq('type', 'folder')
      .eq('parent_id', projectId)
      .is('archived', false)
      .order('order_index', { ascending: true })
      .order('name', { ascending: true })
  );

  return (folders || []).map((f) => ({ id: f.id, name: f.name }));
}

export async function getListsByProjectId(
  projectId: string
): Promise<Array<{ id: string; name: string; folder_id: string | null }>> {
  // Get root project ID first
  const rootProjectId = await getRootProjectId(projectId);
  if (!rootProjectId) return [];

  // Get all spaces in the project hierarchy
  const { data: allSpaces } = await withRetry(() => supabase.from('spaces').select('id, name, type, parent_id, project_id').is('archived', false));

  if (!allSpaces) return [];

  // Filter to get lists that belong to this project hierarchy
  const lists: Array<{ id: string; name: string; folder_id: string | null }> = [];
  const itemsToCheck = [rootProjectId];
  const checked = new Set<string>();

  while (itemsToCheck.length > 0) {
    const currentId = itemsToCheck.pop()!;
    if (checked.has(currentId)) continue;
    checked.add(currentId);

    const children = allSpaces.filter((s) => s.parent_id === currentId);
    for (const child of children) {
      if (child.type === 'list') {
        const folderId =
          child.parent_id && allSpaces.find((s) => s.id === child.parent_id && s.type === 'folder')
            ? child.parent_id
            : null;
        lists.push({ id: child.id, name: child.name, folder_id: folderId });
      } else {
        itemsToCheck.push(child.id);
      }
    }
  }

  return lists;
}

export async function getListsByFolderId(folderId: string): Promise<Array<{ id: string; name: string }>> {
  const { data: children } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id, name, type')
      .eq('parent_id', folderId)
      .eq('type', 'list')
      .is('archived', false)
      .order('order_index', { ascending: true })
      .order('name', { ascending: true })
  );

  const lists = (children || []).filter((c: { id: string; name: string; type: string }) => c.type === 'list');
  return lists.map((l) => ({ id: l.id, name: l.name }));
}

export async function getAllProjects(orgId?: string): Promise<Array<{ id: string; name: string; description?: string; parent_id?: string | null }>> {
  const { cachedQuery } = await import('../utils/queryCache');
  const cacheKey = orgId ? `getAllProjects_${orgId}` : 'getAllProjects';

  return cachedQuery(cacheKey, async () => {
    let query = supabase
      .from('spaces')
      .select('id, name, description, org_id, parent_id')
      .eq('type', 'project')
      .is('archived', false);

    if (orgId) {
      query = query.eq('org_id', orgId);
    }

    const { data: projects } = await withRetry(() =>
      query.order('order_index', { ascending: true }).order('name', { ascending: true })
    );

    // Remove duplicates by name (keep the first one encountered)
    const uniqueProjects = new Map<string, { id: string; name: string; description?: string; parent_id?: string | null }>();
    (projects || []).forEach((p: Record<string, unknown>) => {
      if (!uniqueProjects.has(p.name as string)) {
        uniqueProjects.set(p.name as string, {
          id: p.id as string,
          name: p.name as string,
          description: (p.description as string) || undefined,
          parent_id: (p.parent_id as string | null) || null,
        });
      }
    });
    return Array.from(uniqueProjects.values());
  });
}

export async function getAllCategories(orgId?: string): Promise<Array<{ id: string; name: string }>> {
  const { cachedQuery } = await import('../utils/queryCache');
  const cacheKey = orgId ? `getAllCategories_${orgId}` : 'getAllCategories';

  return cachedQuery(cacheKey, async () => {
    let query = supabase
      .from('spaces')
      .select('id, name')
      .eq('type', 'category')
      .is('parent_id', null)
      .is('archived', false);

    if (orgId) {
      query = query.eq('org_id', orgId);
    }

    const { data: categories } = await withRetry(() =>
      query.order('order_index', { ascending: true })
    );

    return (categories || []).map((c: { id: string; name: string }) => ({
      id: c.id,
      name: c.name,
    }));
  });
}

export async function getAllFolders(
  projects?: Array<{ id: string; name: string }>
): Promise<Array<{ id: string; name: string; project_id: string; orderIndex?: number }>> {
  const { cachedQuery } = await import('../utils/queryCache');

  // OPTIMIZATION: Accept projects as parameter to avoid redundant query
  const projectsToUse = projects || (await getAllProjects());
  if (projectsToUse.length === 0) return [];

  // Create cache key from project IDs
  const projectsKey = projectsToUse
    .map((p) => p.id)
    .sort()
    .join(',');
  const cacheKey = `getAllFolders:${projectsKey}`;

  return cachedQuery(cacheKey, async () => {
    // Fetch folders for all projects in parallel
    const folderPromises = projectsToUse.map(async (project) => {
      try {
        const { data: folders } = await withRetry(() =>
          supabase
            .from('spaces')
            .select('id, name, parent_id, order_index')
            .eq('type', 'folder')
            .eq('parent_id', project.id)
            .is('archived', false)
            .order('order_index', { ascending: true })
            .order('name', { ascending: true })
        );
        return folders || [];
      } catch (error) {
        handleError(error, { level: 'warning', operation: 'loadProjectFolders', message: `Project ${project.id} (${project.name}) not found, skipping folders` });
        return [];
      }
    });

    const foldersArrays = await Promise.all(folderPromises);
    const allFolders: Array<{ id: string; name: string; project_id: string; orderIndex?: number }> = [];

    foldersArrays.forEach((folders) => {
      allFolders.push(
        ...folders.map((f) => ({
          id: f.id,
          name: f.name,
          project_id: f.parent_id || '',
          orderIndex: f.order_index ?? undefined,
        }))
      );
    });

    return allFolders;
  });
}

export async function getAllLists(
  projects?: Array<{ id: string; name: string }>
): Promise<Array<{ id: string; name: string; project_id: string; folder_id: string | null; orderIndex?: number }>> {
  const { cachedQuery } = await import('../utils/queryCache');

  // OPTIMIZATION: Accept projects as parameter to avoid redundant query
  const projectsToUse = projects || (await getAllProjects());
  if (projectsToUse.length === 0) return [];

  // Create cache key from project IDs
  const projectsKey = projectsToUse
    .map((p) => p.id)
    .sort()
    .join(',');
  const cacheKey = `getAllLists:${projectsKey}`;

  return cachedQuery(cacheKey, async () => {
    // Get all spaces to build hierarchy - cache this expensive query
    const allSpaces = await cachedQuery('getAllSpaces:hierarchy', async () => {
      const { data } = await withRetry(() => supabase.from('spaces').select('id, name, type, parent_id, project_id, order_index').is('archived', false));
      return data || [];
    });

    if (!allSpaces || allSpaces.length === 0) return [];

    const allLists: Array<{
      id: string;
      name: string;
      project_id: string;
      folder_id: string | null;
      orderIndex?: number;
    }> = [];

    // For each project, find all lists in its hierarchy
    for (const project of projectsToUse) {
      const itemsToCheck = [project.id];
      const checked = new Set<string>();

      while (itemsToCheck.length > 0) {
        const currentId = itemsToCheck.pop()!;
        if (checked.has(currentId)) continue;
        checked.add(currentId);

        const children = allSpaces.filter((s) => s.parent_id === currentId);
        for (const child of children) {
          if (child.type === 'list') {
            // Find folder_id and project_id
            let folderId: string | null = null;
            let rootProjectId = project.id;

            // Traverse up to find folder and root project
            let parentId = child.parent_id;
            while (parentId) {
              const parent = allSpaces.find((s) => s.id === parentId);
              if (!parent) break;

              if (parent.type === 'project') {
                rootProjectId = parent.id;
                break;
              } else if (parent.type === 'folder') {
                folderId = parent.id;
              }
              parentId = parent.parent_id;
            }

            allLists.push({
              id: child.id,
              name: child.name,
              project_id: rootProjectId,
              folder_id: folderId,
              orderIndex: child.order_index ?? undefined,
            });
          } else {
            itemsToCheck.push(child.id);
          }
        }
      }
    }

    return allLists;
  });
}

// OPTIMIZATION: Single query to get all items for a space (unified structure)
export async function getAllSpaceItems(spaceId: string): Promise<{
  items: Array<Project & { id: string }>;
  folders: Array<{ id: string; name: string }>;
}> {
  const { cachedQuery } = await import('../utils/queryCache');

  return cachedQuery(`getAllSpaceItems:${spaceId}`, async () => {
    const rootProjectId = await getRootProjectId(spaceId);
    if (!rootProjectId) {
      throw new Error(`Cannot determine root project for getAllSpaceItems`);
    }

    // Get all spaces in the hierarchy - cache this expensive query
    const allSpaces = await cachedQuery('getAllSpaces:all', async () => {
      const { data } = await withRetry(() => supabase.from('spaces').select('*').is('archived', false));
      return data || [];
    });

    if (!allSpaces || allSpaces.length === 0) return { items: [], folders: [] };

    const folders: Array<{ id: string; name: string }> = [];
    const itemsToCheck = [rootProjectId];
    const checked = new Set<string>();

    while (itemsToCheck.length > 0) {
      const currentId = itemsToCheck.pop()!;
      if (checked.has(currentId)) continue;
      checked.add(currentId);

      const children = allSpaces.filter((s) => s.parent_id === currentId);
      for (const child of children) {
        if (child.type === 'folder') {
          folders.push({ id: child.id, name: child.name });
          itemsToCheck.push(child.id);
        } else if (child.type === 'project') {
          itemsToCheck.push(child.id);
        }
      }
    }

    // Get all items directly under root project
    const { data: items } = await withRetry(() =>
      supabase
        .from('spaces')
        .select('*')
        .eq('parent_id', rootProjectId)
        .in('type', ['document', 'chat', 'voice_channel', 'spreadsheet', 'presentation'])
        .order('order_index', { ascending: true })
        .order('name', { ascending: true })
    );

    folders.sort((a, b) => {
      const aSpace = allSpaces.find((s) => s.id === a.id);
      const bSpace = allSpaces.find((s) => s.id === b.id);
      const orderA = aSpace?.order_index ?? Infinity;
      const orderB = bSpace?.order_index ?? Infinity;
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name);
    });

    return {
      items: (items || []).map(convertSpaceFromSupabase) as Array<Project & { id: string }>,
      folders,
    };
  });
}

// Load single item by ID
export async function getDocumentById(id: string): Promise<(Document & { id: string }) | null> {
  const { data: doc } = await withRetry(() => supabase.from('spaces').select('*').eq('id', id).eq('type', 'document').single());
  return doc ? (convertSpaceFromSupabase(doc) as Document & { id: string }) : null;
}

export async function getChatById(id: string): Promise<(Chat & { id: string }) | null> {
  const { data: chat } = await withRetry(() => supabase.from('spaces').select('*').eq('id', id).eq('type', 'chat').single());
  return chat ? (convertSpaceFromSupabase(chat) as Chat & { id: string }) : null;
}

export async function getVoiceChannelById(id: string): Promise<(VoiceChannel & { id: string }) | null> {
  const { data: channel } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('*')
      .eq('id', id)
      .eq('type', 'voice_channel')
      .single()
  );
  return channel ? (convertSpaceFromSupabase(channel) as VoiceChannel & { id: string }) : null;
}

export async function getSpreadsheetById(id: string): Promise<(Spreadsheet & { id: string }) | null> {
  const { data: sheet } = await withRetry(() => supabase.from('spaces').select('*').eq('id', id).eq('type', 'spreadsheet').single());
  return sheet ? (convertSpaceFromSupabase(sheet) as Spreadsheet & { id: string }) : null;
}

export async function getPresentationById(id: string): Promise<(Presentation & { id: string }) | null> {
  const { data: pres } = await withRetry(() => supabase.from('spaces').select('*').eq('id', id).eq('type', 'presentation').single());
  return pres ? (convertSpaceFromSupabase(pres) as Presentation & { id: string }) : null;
}

export async function getRootProjectIds(): Promise<string[]> {
  const { data, error } = await withRetry(() =>
    supabase.from('spaces').select('id').eq('type', 'project').is('archived', false)
  );
  if (error) throw error;
  return (data || []).map(s => s.id);
}

export async function getTaskCount(spaceId?: string): Promise<number> {
  let query = supabase.from('tasks').select('*', { count: 'exact', head: true });
  if (spaceId) {
    query = query.eq('space_id', spaceId);
  }
  const { count, error } = await withRetry(() => query);
  if (error) throw error;
  return count || 0;
}

export async function getSpaceChildren(parentId: string): Promise<SpaceRecord[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id, name, type, parent_id, order_index')
      .eq('parent_id', parentId)
      .is('archived', false)
      .order('order_index', { ascending: true })
      .order('name', { ascending: true })
  );
  if (error) throw error;
  return data || [];
}

